/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { CloseCircleOutlined } from '@ant-design/icons';
import { COMMON_ENV } from '@common/constants/common-env.constant';
import { CONST } from '@common/constants/const.constant';
import { THEME } from '@common/constants/theme.constant';
import { Field, IFieldProps } from '@common/general-ui/field/field.component';
import { Input } from '@common/general-ui/input/input.component';
import { consoleService } from '@common/services/console.service';
import { generateId } from '@common/utils/generate-id.util';
import { Global } from '@emotion/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactTestUtils from 'react-dom/test-utils';
import AddressSearch from 'react-loqate';
import { css } from 'twin.macro';
import { InputV2 } from '@common/general-ui/input-v2/input-v2.component';

export interface IInputLoqateValue {
    addressLine1: string;
    addressLine2: string;
    addressLine3: string;
    city: string;
    postCode: string;
    originalLabel: string;
    label: string;
    country: string;
}
export const EMPTY_LOQATE_ADDRESS: IInputLoqateValue = {
    addressLine1: '',
    addressLine2: '',
    addressLine3: '',
    country: CONST.DEFAULT_COUNTRY_ABBR,
    city: '',
    postCode: '',
    originalLabel: '',
    label: '',
};
export type IInputLoqateChangeHandler = (arg: { name: string; value: IInputLoqateValue }) => void;
export interface IInputLoqateProps<TName = string> extends IFieldProps<IInputLoqateValue, TName> {
    className?: string;
    onChange?: IInputLoqateChangeHandler;
    onBlur?: (e: React.FocusEvent) => void;
    apiKey?: string;
}

export const InputLoqate = (props: IInputLoqateProps): JSX.Element => {
    const html_id = useMemo(() => generateId('html_id'), []);
    const { onChange, name, placeholder, value, errorMessage, apiKey = COMMON_ENV.REACT_APP_LOQATE_API_KEY || '' } = props;
    const [address, setAddress] = useState(value);

    useEffect(() => {
        setAddress(value);
    }, [value]);

    const handleClear = useCallback((): void => {
        setAddress(EMPTY_LOQATE_ADDRESS);

        const loqateInput = document.getElementById(html_id) as HTMLInputElement;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (loqateInput) {
            loqateInput.value = '';
            ReactTestUtils.Simulate.change(loqateInput);
        }

        onChange &&
            onChange({
                name,
                value: EMPTY_LOQATE_ADDRESS,
            });
    }, [html_id, name, onChange]);

    const renderInput = useCallback(
        (inputProps: any): JSX.Element => {
            return (
                <InputV2
                    readOnly={props.readOnly}
                    label={props.label}
                    type="text"
                    name={name}
                    placeholder={placeholder}
                    {...inputProps}
                    id={html_id}
                    required={props.required}
                    value={address?.label || inputProps.value}
                    suffix={props.readOnly ? <></> : <CloseCircleOutlined tw="absolute right-2" onClick={handleClear} />}
                />
            );
        },
        [address?.label, handleClear, html_id, name, placeholder, props.label, props.readOnly, props.required],
    );

    return (
        <Field footerCss={styles.footer} labelCss={styles.label} name={`${name}-top`} errorMessage={errorMessage} helperText={<div>{props.helperText}</div>}>
            <div tw="relative">
                <AddressSearch
                    className={props.className}
                    locale="en_GB"
                    countries={['GB']}
                    debounce={800}
                    apiKey={apiKey}
                    onSelect={useCallback(
                        (address: any): void => {
                            consoleService.log('address', address);
                            const arg: IInputLoqateValue = {
                                city: address.City,
                                addressLine1: address.Line1,
                                addressLine2: address.Line2,
                                addressLine3: [address.Line3, address.Line4, address.Line5].filter((f) => f).join('/'),

                                country: address.CountryIso2,
                                postCode: address.PostalCode,
                                label: [
                                    address.Line1,
                                    [address.Line2, address.Line3, address.Line4, address.Line5].filter((f) => f).join('/'),
                                    address.City,
                                    address.PostalCode,
                                ]
                                    .filter((f) => f)
                                    .join(', '),
                                originalLabel: address.Label,
                            };
                            setAddress(arg);
                            onChange && onChange({ name, value: arg });
                        },
                        [name, onChange],
                    )}
                    components={useMemo(() => {
                        return {
                            Input: renderInput,
                        };
                    }, [renderInput])}
                    classes={useMemo(() => ({ list: 'address-options' }), [])}
                />
            </div>
        </Field>
    );
};

export const getInputLoqateLabel = (address: IInputLoqateValue): string => {
    const res = [
        address.addressLine1,
        [address.addressLine2, address.addressLine3].filter((f) => f).join('/'),
        address.city,
        address.postCode,
    ]
        .filter((f) => f)
        .join(', ');
    return res;
};

const styles = {
  label: css`
    font-weight: 400;
  `,
  footer: css`
    margin-top: 0;
  `
};
